import EventDispatcher from "ui/EventDispatcher";
import Utils from "ui/helpers/Utils";

/**
 * Canvas Selection Controller
 */
export default class CanvasSelection extends EventDispatcher {
	/**
	 * Class constructor
	 *
	 * @param {Canvas} canvas
	 * @param {T_CanvasElement} fillCanvas
	 */
	constructor(canvas, fillCanvas) {
		super();
		/**
		 * @type {Canvas}
		 * @private
		 */
		this._canvas = canvas;

		/**
		 * @type {T_CanvasElement}
		 * @private
		 */
		this._fillCanvas = fillCanvas;

		/**
		 * The canvas for hitting selection under cursor. Size is equal to the viewport
		 *
		 * @type {T_CanvasElement}
		 * @private
		 */
		this._hitTestCanvas = Utils.createCanvas('', 100, 100);

		/**
		 * @type {{
		 *  drawType: string,
		 *  show: boolean,
		 *  isClosed: boolean,
		 *  dashOffset: number,
		 *  dashOffsetTimer: number,
		 *  isAddMode: boolean,
		 *  path: ?Array<T_Point>,
		 *  newPath: ?Array<T_Point>,
		 *  x: number, y: number, width: number, height: number,
		 *  fragment: ?T_CanvasElement,
		 *  fragmentData: ?T_DataSetItem,
		 *  isPointerOver: boolean,
		 *  isPolyStarted: boolean,
		 *  startX: number, startY: number
		 *  }}
		 * @private
		 */
		this._selection = {
			drawType: 'lasso',
			show: false,
			dashOffset: 0,
			dashOffsetTimer: 0,
			isClosed: false,
			isAddMode: true,
			path: null,
			newPath: null,
			x: 0,
			y: 0,
			width: 0,
			height: 0,
			fragment: null,
			fragmentData: null,
			isPointerOver: false,
			isMoving: false,
			isPolyStarted: false,
			startX: 0,
			startY: 0
		};

		/**
		 * @type {{
		 *  fragment: T_CanvasElement,
		 *  fromX: number, fromY: number
		 *  }}
		 * @private
		 */
		this._buffer = {
			fragment: null,
			fromX: 0,
			fromY: 0
		};

		this._handlers = {
			updateSelectionDashOffset: this._updateSelectionDashOffset.bind(this),
		};
	}

	/**
	 * Sets work canvas size
	 *
	 * @param {number} width
	 * @param {number} height
	 * @returns {CanvasSelection}
	 */
	setWorkAreaSize(width, height) {
		const canvas = this._hitTestCanvas.el;
		canvas.width = width;
		canvas.height = height;
		return this;
	}

	/**
	 * Returns TRUE if there is extracted image fragment
	 *
	 * @returns {boolean}
	 */
	get hasFragment() {
		return this._selection.fragment !== null;
	}

	/**
	 * Returns TRUE if there is selection
	 *
	 * @returns {boolean}
	 */
	get hasSelection() {
		return !!(this._selection.show && this._selection.path && this._selection.isClosed);
	}

	/**
	 * Returns TRUE if there is selection
	 *
	 * @returns {boolean}
	 */
	get isRectangularSelection() {
		let sel = this._selection,
			path = sel.path;
		return sel.show
			&& path
			&& path.length === 4
			&& path[0].x === path[3].x
			&& path[0].y === path[1].y
			&& path[1].x === path[2].x
			&& path[2].y === path[3].y;
	}

	/**
	 * Returns TRUE if the pointer is over the selection area
	 *
	 * @returns {boolean}
	 */
	get isPointerOverSelection() {
		return this._selection.show && this._selection.isPointerOver;
	}

	/**
	 * Returns TRUE if currently enabled mode for moving selected area
	 *
	 * @returns {boolean}
	 */
	get isMovingMode() {
		return this._selection.isMoving;
	}

	/**
	 * Returns TRUE if currently new selection path is drawing
	 *
	 * @returns {boolean}
	 */
	get isDrawingMode() {
		return !!(this._selection.show && this._selection.newPath && this._selection.newPath.length > 1);
	}

	/**
	 * Returns TRUE if polygonal selection draw mode enabled
	 *
	 * @returns {boolean}
	 */
	get isPolygonalDrawMode() {
		return this._selection.drawType === 'poly' && this._selection.isPolyStarted;
	}

	/**
	 * Returns selection bounding rectangle
	 *
	 * @returns {{x: number, y: number, width: number, height: number}}
	 */
	get selectedAreaRect() {
		let sel = this._selection;
		return {
			x: sel.x,
			y: sel.y,
			width: sel.width,
			height: sel.height,
		};
	}

	/**
	 * Sets flag which indicates that pointer is over the selection
	 *
	 * @param {boolean} flag
	 * @returns {CanvasSelection}
	 */
	setPointerOverSelectionFlag(flag) {
		this._selection.isPointerOver = flag === true;
		return this;
	}

	/**
	 * Applies round X and Y of the point
	 *
	 * @param {T_Point} pos
	 * @returns {void}
	 * @private
	 */
	_roundPoint(pos) {
		pos.x = Math.round(pos.x);
		pos.y = Math.round(pos.y);
	}

	/**
	 * Start drawing new selection or moving already drawn selection area
	 *
	 * @param {T_Point} pos
	 * @param {boolean} isCtrl
	 * @param {string} drawType Possible values: free, poly, rect
	 * @returns {CanvasSelection}
	 */
	processPointerDown(pos, isCtrl, drawType = 'free') {
		this._roundPoint(pos);
		let sel = this._selection;
		sel.drawType = drawType;
		if (sel.path !== null && sel.isPointerOver) {
			// move current selection
			sel.startX = sel.x;
			sel.startY = sel.y;
			sel.isMoving = true;
			return this;
		}
		if (sel.drawType === 'poly' && sel.isPolyStarted) {
			// do nothing here for polygonal lasso, if it already started
			return this;
		}
		this.releaseFragment();
		sel.show = true;
		sel.isMoving = false;
		sel.isPointerOver = false;
		sel.isPolyStarted = sel.drawType === 'poly';
		sel.newPath = [pos];
		sel.path = null;
		this._updateSelectionDashOffset();
		sel.isAddMode = !isCtrl;
		return this;
	}

	/**
	 * Draws selection or moves the selected area
	 *
	 * @param {T_Point} pos
	 * @param {number} dx
	 * @param {number} dy
	 * @param {boolean} isCtrl
	 * @returns {CanvasSelection}
	 */
	processPointerMove(pos, dx, dy, isCtrl) {
		this._roundPoint(pos);
		let sel = this._selection;
		if (sel.isPolyStarted) {
			// polygonal lasso mode, no not save point while pointer is moving
			return this;
		}
		if (sel.isMoving) {
			if (isCtrl && !sel.fragment) {
				this.extractFragment();
			}
			sel.x = Math.round(sel.startX + dx);
			sel.y = Math.round(sel.startY + dy);
		} else if (sel.newPath !== null) {
			if (sel.drawType === 'rect') {
				sel.newPath[1] = pos;
			} else {
				sel.newPath.push(pos);
			}
		}
		return this;
	}

	/**
	 * Finishes selection or draw new line for polygonal selection type
	 *
	 * @param {T_Point} pos
	 * @return {boolean}
	 */
	processPointerUp(pos) {
		this._roundPoint(pos);
		let sel = this._selection;
		if (sel.isMoving) {
			return false;
		}
		if (sel.newPath === null) {
			// there is no new selection, nothing do here
			return false;
		}
		if (sel.isPolyStarted) {
			// add point to selection for polygonal lasso
			sel.newPath.push(pos);
			if (sel.newPath.length > 3) {
				let startPoint = sel.newPath[0],
					dx = Math.abs(startPoint.x - pos.x),
					dy = Math.abs(startPoint.y - pos.y);
				if (dx < 4 && dy <= 4) {
					sel.isPolyStarted = false;
				}
			}
			if (sel.isPolyStarted) {
				return false;
			}
		}
		if (sel.drawType === 'rect') {
			if (sel.newPath.length < 2) {
				sel.isPolyStarted = false;
				sel.newPath = null;
				return false;
			}
			// create selection rectangle from two points
			let newPath = sel.newPath,
				minX = Math.min(newPath[0].x, newPath[1].x),
				minY = Math.min(newPath[0].y, newPath[1].y),
				maxX = Math.max(newPath[0].x, newPath[1].x),
				maxY = Math.max(newPath[0].y, newPath[1].y);
			sel.path = [
				{x: minX, y: minY},
				{x: maxX, y: minY},
				{x: maxX, y: maxY},
				{x: minX, y: maxY},
			];
			sel.isClosed = true;
		} else {
			if (sel.newPath.length < 3) {
				sel.isPolyStarted = false;
				sel.newPath = null;
				return false;
			}
			if (sel.path !== null) {
				//TODO
			} else {
				sel.path = sel.newPath;
				sel.isClosed = true;
			}
		}

		sel.isPolyStarted = false;
		sel.newPath = null;
		let minX = Number.MAX_VALUE, minY = Number.MAX_VALUE,
			maxX = 0, maxY = 0;
		for (let p of sel.path) {
			p.x < minX && (minX = p.x);
			p.y < minY && (minY = p.y);
			p.x > maxX && (maxX = p.x);
			p.y > maxY && (maxY = p.y);
		}

		minX = Math.max(Math.floor(minX), 0);
		minY = Math.max(Math.floor(minY), 0);
		maxX = Math.min(Math.ceil(maxX), this._fillCanvas.el.width);
		maxY = Math.min(Math.ceil(maxY), this._fillCanvas.el.height);
		if (maxX - minX < 1 || maxY - minY < 1) {
			sel.path = null;
			sel.newPath = null;
			return false;
		}
		sel.x = minX;
		sel.y = minY;
		sel.width = maxX - minX;
		sel.height = maxY - minY;

		for (let p of sel.path) {
			p.x -= minX;
			p.y -= minY;
		}
		return true;
	}

	/**
	 * Makes flood fill for the
	 *
	 * @param {Color} color
	 * @returns {boolean}
	 */
	fill(color) {
		if (!this.hasSelection) {
			return false;
		}
		let ctx = this._fillCanvas.ctx;
		let sel = this._selection;
		ctx.save();
		ctx.fillStyle = color.rgbaCss;
		ctx.beginPath();
		let started = false;
		for (let p of this._selection.path) {
			if (started) {
				ctx.lineTo(sel.x + p.x, sel.y + p.y);
			} else {
				started = true;
				ctx.moveTo(sel.x + p.x, sel.y + p.y);
			}
		}
		ctx.closePath();
		ctx.fillStyle = color.rgbCss;
		ctx.globalCompositeOperation = 'destination-out';
		ctx.fill();
		ctx.fillStyle = color.rgbaCss;
		ctx.globalCompositeOperation = 'source-over';
		ctx.fill();
		ctx.restore();
		return true;
	}

	/**
	 * Applies selection clipping area (if needed) and call passed function, then restores canvas state
	 *
	 * @param {CanvasRenderingContext2D} ctx
	 * @param {function()} drawFunc
	 * @returns {CanvasSelection}
	 */
	applyClip(ctx, drawFunc) {
		let flag = this.hasSelection;
		if (flag) {
			ctx.save();
			this._buildSourceSelectionPath(ctx, this._selection.x, this._selection.y, this._selection.path);
			ctx.clip();
		}
		drawFunc();
		if (flag) {
			ctx.restore();
		}
		return this;
	}

	/**
	 * Sets selection path
	 *
	 * @param {Array<T_Point>} path
	 * @returns {CanvasSelection}
	 */
	setPath(path) {
		if (path.length < 3) {
			return this;
		}
		this.dropSelection();

		path.map(this._roundPoint.bind(this));

		let sel = this._selection;
		sel.show = true;
		sel.isClosed = true;

		let minX = Number.MAX_VALUE, maxX = 0, minY = Number.MAX_VALUE, maxY = 0;
		for (let p of path) {
			if (p.x < minX) {
				minX = p.x;
			}
			if (p.y < minY) {
				minY = p.y;
			}
			if (p.x > maxX) {
				maxX = p.x;
			}
			if (p.y > maxY) {
				maxY = p.y;
			}
		}
		sel.x = minX;
		sel.y = minY;
		sel.width = maxX - minX;
		sel.height = maxY - minY;
		sel.path = path;
		this._updateSelectionDashOffset();
		return this;
	}

	/**
	 * Drops current selection
	 *
	 * @param {boolean} [releaseFragment]
	 * @returns {CanvasSelection}
	 */
	dropSelection(releaseFragment = true) {
		let sel = this._selection;
		if (sel.fragment) {
			if (releaseFragment) {
				this.releaseFragment();
			}
			sel.fragment = null;
			sel.fragmentData = null;
		}
		sel.show = false;
		sel.isClosed = false;
		sel.dashOffset = 0;
		sel.isAddMode = true;
		sel.isPointerOver = false;
		sel.isPolyStarted = false;
		sel.path = null;
		sel.newPath = null;
		sel.x = 0;
		sel.y = 0;
		sel.width = 0;
		sel.height = 0;
		if (sel.dashOffsetTimer > 0) {
			clearTimeout(sel.dashOffsetTimer);
			sel.dashOffsetTimer = 0;
		}
		return this;
	}

	/**
	 * Extracts selected fragment
	 *
	 * @returns {boolean}
	 */
	extractFragment() {
		if (!this.hasSelection) {
			return false;
		}
		let sel = this._selection;
		if (sel.fragment !== null) {
			return false;
		}
		sel.fragment = this._getSelectedFragment();
		sel.fragmentData = this._canvas.dataSet.setData(sel.fragment.el.toDataURL('image/png'));
		this.clear(true);
		return true;
	}

	/**
	 * Merges selected fragment with the layer
	 *
	 * @returns {boolean}
	 */
	releaseFragment() {
		if (!this.hasSelection) {
			return false;
		}
		let sel = this._selection;
		if (sel.fragment === null) {
			return false;
		}
		let ctx = this._fillCanvas.ctx;
		ctx.drawImage(sel.fragment.el, 0, 0, sel.width, sel.height, sel.x, sel.y, sel.width, sel.height);

		sel.fragment = null;
		sel.fragmentData = null;

		this._canvas.redrawArea(sel.x, sel.y, sel.width, sel.height)
			.saveCurrentLayerImage();
		return true;
	}

	/**
	 * Copies selected fragment
	 *
	 * @returns {boolean}
	 */
	copyFragment() {
		if (!this.hasSelection) {
			return false;
		}

		let sel = this._selection;
		let buffer = this._buffer;

		if (sel.fragment) {
			buffer.fragment = sel.fragment;
		} else {
			buffer.fragment = this._getSelectedFragment();
		}

		buffer.fromX = sel.x;
		buffer.fromY = sel.y;
		return true;
	}

	/**
	 * Pastes fragment from buffer into the center of current selection or viewport
	 *
	 * @param {number} centerX
	 * @param {number} centerY
	 * @param {boolean} [toOriginalPos]
	 * @returns {boolean}
	 */
	pasteFragment(centerX, centerY, toOriginalPos = false) {
		let buffer = this._buffer;
		if (!buffer.fragment) {
			return false;
		}

		let sel = this._selection,
			x, y;

		if (toOriginalPos) {
			x = buffer.fromX;
			y = buffer.fromY;
		} else {
			if (sel.show && sel.path !== null) {
				// get center of the selection
				x = sel.x + sel.width / 2;
				y = sel.y + sel.height / 2;
			} else {
				// get center of the visible area
				x = centerX;
				y = centerY;
			}
		}

		this.dropSelection();

		let fragEl = buffer.fragment.el;
		sel.show = true;
		sel.isClosed = true;
		sel.isPointerOver = false;
		sel.x = Math.round(x - fragEl.width / 2);
		sel.y = Math.round(y - fragEl.height / 2);
		sel.width = fragEl.width;
		sel.height = fragEl.height;
		sel.path = [
			{x: 0, y: 0},
			{x: fragEl.width, y: 0},
			{x: fragEl.width, y: fragEl.height},
			{x: 0, y: fragEl.height},
		];
		sel.fragment = buffer.fragment;
		sel.fragmentData = this._canvas.dataSet.setData(sel.fragment.el.toDataURL('image/png'));
		this._updateSelectionDashOffset();
		this._canvas.redrawArea(sel.x, sel.y, sel.width, sel.height);
		return true;
	}

	/**
	 * Clears selected area
	 *
	 * @param {boolean} keepFragment
	 * @returns {boolean}
	 */
	clear(keepFragment = false) {
		if (!this.hasSelection) {
			return false;
		}
		let sel = this._selection;
		if (sel.fragment && keepFragment === false) {
			// remove extracted fragment
			sel.fragment = null;
			sel.fragmentData = null;
		}

		this.clearSelectionInCanvas(this._fillCanvas.ctx);
		this._canvas.redrawArea(sel.x, sel.y, sel.width, sel.height);
		return true;
	}

	/**
	 * Clears selected area for the specified canvas
	 *
	 * @param {CanvasRenderingContext2D} ctx
	 * @returns {boolean}
	 */
	clearSelectionInCanvas(ctx) {
		if (!this.hasSelection) {
			return false;
		}

		const sel = this._selection;
		ctx.save();
		this._buildSourceSelectionPath(ctx, sel.x, sel.y, sel.path);
		ctx.clip();
		ctx.fillStyle = '#000';
		ctx.globalCompositeOperation = 'destination-out';
		ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
		ctx.restore();

		return true;
	}

	/**
	 * Tests if the specified point is over the current selection
	 *
	 * @param {number} x
	 * @param {number} y
	 * @returns {boolean}
	 */
	testPoint(x, y) {
		const testCtx = this._hitTestCanvas.ctx;
		const testEl = this._hitTestCanvas.el;

		if (x < 0 || y < 0 || x > testEl.width || y > testEl.height) {
			return false;
		}
		let color = testCtx.getImageData(x, y, 1, 1).data;
		return color[0] === 255 && color[1] === 0 && color[2] === 0;
	}

	/**
	 * Saves state of selection for History
	 *
	 * @param {T_State} rec
	 * @returns {CanvasSelection}
	 */
	saveState(rec) {
		let sel = this._selection;
		if (sel.path !== null) {
			rec.selection = {
				x: sel.x,
				y: sel.y,
				width: sel.width,
				height: sel.height,
				path: sel.path.slice(),
				fragmentData: sel.fragmentData
			};
		}
		return this;
	}

	/**
	 * Restores state of canvas selection
	 *
	 * @param {T_State} rec
	 * @returns {Promise<void>}
	 */
	async restoreState(rec) {
		if (rec.selection !== null) {
			let srcSel = rec.selection,
				sel = this._selection;
			this.dropSelection(false);
			sel.show = true;
			sel.isClosed = true;
			sel.x = srcSel.x;
			sel.y = srcSel.y;
			sel.width = srcSel.width;
			sel.height = srcSel.height;
			sel.path = srcSel.path;
			if (srcSel.fragmentData !== null) {
				let canvas = Utils.createCanvas('', srcSel.width, srcSel.height);
				sel.fragment = canvas;
				sel.fragmentData = srcSel.fragmentData;
				await this._drawImageData(canvas, sel.fragmentData);
			}
			this._updateSelectionDashOffset();
		} else if (this._selection.path !== null
			|| this._selection.newPath !== null) {
			this.dropSelection(false);
		}
	}

	/**
	 * Renders selection
	 *
	 * @param {CanvasRenderingContext2D} ctx
	 * @param {number} offsetX
	 * @param {number} offsetY
	 * @param {number} scaleFactor
	 * @returns {CanvasSelection}
	 */
	render(ctx, offsetX, offsetY, scaleFactor) {
		let sel = this._selection;
		if (!sel.show) {
			return this;
		}

		if (sel.fragment !== null) {
			ctx.drawImage(
				sel.fragment.el,
				0, 0, sel.width, sel.height,
				(sel.x - offsetX) * scaleFactor,
				(sel.y - offsetY) * scaleFactor,
				sel.width * scaleFactor,
				sel.height * scaleFactor,
			);
		}

		if (sel.path !== null) {
			ctx.save();
			ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
			ctx.lineWidth = 1;
			this._buildDisplaySelectionPath(ctx, sel.x, sel.y, sel.path, sel.isClosed, offsetX, offsetY, scaleFactor);
			ctx.stroke();
			ctx.restore();

			ctx.save();
			ctx.globalCompositeOperation = 'difference';
			ctx.setLineDash([5, 5]);
			ctx.lineDashOffset = sel.dashOffset;
			ctx.strokeStyle = 'rgba(255, 255, 255, 1)';
			ctx.lineWidth = 1;
			this._buildDisplaySelectionPath(ctx, sel.x, sel.y, sel.path, sel.isClosed, offsetX, offsetY, scaleFactor);
			ctx.stroke();
			ctx.restore();

			let testCtx = this._hitTestCanvas.ctx;
			testCtx.clearRect(0, 0, this._hitTestCanvas.el.width, this._hitTestCanvas.el.height);
			testCtx.fillStyle = '#f00';
			this._buildDisplaySelectionPath(testCtx, sel.x, sel.y, sel.path, sel.isClosed, offsetX, offsetY, scaleFactor);
			testCtx.fill();
		}

		if (sel.newPath !== null && sel.newPath.length > 1) {
			ctx.save();
			ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
			ctx.lineWidth = 1;
			this._buildDisplaySelectionPath(ctx, 0, 0, sel.newPath, false, offsetX, offsetY, scaleFactor);
			ctx.stroke();
			ctx.restore();

			ctx.save();
			ctx.globalCompositeOperation = 'difference';
			ctx.setLineDash([5, 5]);
			ctx.lineDashOffset = sel.dashOffset;
			ctx.strokeStyle = 'rgba(255, 255, 255, 1)';
			ctx.lineWidth = 1;
			this._buildDisplaySelectionPath(ctx, 0, 0, sel.newPath, false, offsetX, offsetY, scaleFactor);
			ctx.stroke();
			ctx.restore();
		}
		return this;
	}

	/**
	 * Fills currently used Data Set items
	 *
	 * @param {Set<string>} fillMap
	 * @returns {void}
	 */
	fillDataSetKeys(fillMap) {
		if (this._selection.fragmentData) {
			fillMap.add(this._selection.fragmentData.key);
		}
	}

	/**
	 * Loads Image Data and then draw it on the specified canvas
	 *
	 * @private
	 * @param {T_CanvasElement} canvas
	 * @param {T_DataSetItem} imageData
	 * @returns {Promise}
	 */
	_drawImageData(canvas, imageData) {
		return new Promise((resolve) => {
			let img = new Image();
			img.crossOrigin = 'anonymous';
			img.onload = () => {
				let ctx = canvas.ctx,
					el = canvas.el;
				ctx.clearRect(0, 0, el.width, el.height);
				ctx.drawImage(img, 0, 0, el.width, el.height, 0, 0, el.width, el.height);
				resolve();
			};
			img.src = imageData.data;
		});
	}

	/**
	 * Returns selected canvas fragment
	 *
	 * @private
	 * @returns {T_CanvasElement}
	 */
	_getSelectedFragment() {
		let sel = this._selection, ctx;
		let out = Utils.createCanvas('', sel.width, sel.height);
		ctx = out.ctx;
		ctx.save();
		this._buildSourceSelectionPath(ctx, 0, 0, sel.path);
		ctx.clip();
		ctx.drawImage(this._fillCanvas.el, sel.x, sel.y, sel.width, sel.height, 0, 0, sel.width, sel.height);
		return out;
	}

	/**
	 * Updates selection line dash offset
	 *
	 * @returns {void}
	 * @private
	 */
	_updateSelectionDashOffset() {
		let sel = this._selection;
		if (sel.dashOffsetTimer > 0) {
			clearTimeout(sel.dashOffsetTimer);
		}
		sel.dashOffsetTimer = setTimeout(this._handlers.updateSelectionDashOffset, 200);
		sel.dashOffset = (sel.dashOffset + 3) % 100;
		this._canvas.render();
	}

	/**
	 * Creates selection path for Display Canvas
	 *
	 * @param {CanvasRenderingContext2D} ctx
	 * @param {number} fromX
	 * @param {number} fromY
	 * @param {Array<T_Point>} path
	 * @param {boolean} isClosed
	 * @param {number} offsetX
	 * @param {number} offsetY
	 * @param {number} scaleFactor
	 * @private
	 */
	_buildDisplaySelectionPath(ctx, fromX, fromY, path, isClosed, offsetX, offsetY, scaleFactor) {
		let pos;

		if (path.length === 2 && this._selection.drawType === 'rect') {
			let minX = Math.min(path[0].x, path[1].x),
				minY = Math.min(path[0].y, path[1].y),
				maxX = Math.max(path[0].x, path[1].x),
				maxY = Math.max(path[0].y, path[1].y);
			ctx.beginPath();
			ctx.rect(
				(fromX + minX - offsetX) * scaleFactor,
				(fromY + minY - offsetY) * scaleFactor,
				(maxX - minX) * scaleFactor,
				(maxY - minY) * scaleFactor
			);
			return;
		}

		pos = path[0];
		ctx.beginPath();
		ctx.moveTo((fromX + pos.x - offsetX) * scaleFactor, (fromY + pos.y - offsetY) * scaleFactor);
		for (let a = 1, l = path.length; a < l; a++) {
			pos = path[a];
			ctx.lineTo((fromX + pos.x - offsetX) * scaleFactor, (fromY + pos.y - offsetY) * scaleFactor);
		}
		if (isClosed) {
			ctx.closePath();
		}
	}

	/**
	 * Creates selection path for Source (work) Canvas
	 *
	 * @param {CanvasRenderingContext2D} ctx
	 * @param {number} fromX
	 * @param {number} fromY
	 * @param {Array.<T_Point>} path
	 * @private
	 */
	_buildSourceSelectionPath(ctx, fromX, fromY, path) {
		let pos = path[0];
		ctx.beginPath();
		ctx.moveTo(fromX + pos.x, fromY + pos.y);
		for (let a = 1, l = path.length; a < l; a++) {
			pos = path[a];
			ctx.lineTo(fromX + pos.x, fromY + pos.y);
		}
		ctx.closePath();
	}
}
